import { type ApolloError, useApolloClient } from '@apollo/client';
import { useCallback } from 'react';

// Deep import: the '@/common/components' barrel imports hooks, so importing it from a hook is an import cycle.
import { parsePeriodKey } from '@/common/components/performance-review/period-select/period';
import { toast } from '@/common/components/shared/toaster/toast';
import {
  GetEmployeeDocument,
  type PerformanceReviewExclusionChoiceInput,
  useUpdateEmployeeOffBoardingMutation,
} from '@/common/graphql';
import type { EmployeeOffBoardingType } from '@/common/types';
import { ApiUtils, StringUtils } from '@/common/utils';

interface OffBoardingPersistence {
  employeeId: string;
}

/**
 * The choice is sent only after HR picked a value in the select. Leaving it out keeps the stored period on the
 * server, moved to follow a changed Last working date; an object with a null period clears it.
 */
export const toPerformanceReviewExclusionInput = (
  formData: Pick<EmployeeOffBoardingType, 'performanceReviewExclusion' | 'performanceReviewExclusionChanged'>,
): PerformanceReviewExclusionChoiceInput | undefined =>
  formData.performanceReviewExclusionChanged
    ? { period: parsePeriodKey(formData.performanceReviewExclusion) }
    : undefined;

export const useEmployeeOffBoardingPersistence = ({ employeeId }: OffBoardingPersistence) => {
  const client = useApolloClient();
  const onCompleted = useCallback(() => {
    toast.success();
    client.refetchQueries({
      include: [GetEmployeeDocument],
    });
  }, [client]);
  const onError = (error: ApolloError) => ApiUtils.handleApiError(error);

  const [updateRecord, { loading: updating }] = useUpdateEmployeeOffBoardingMutation({
    onCompleted,
    onError: error => onError(error),
  });

  const onOffBoardingUpdate = useCallback(
    async (formData: EmployeeOffBoardingType, onSuccess: VoidFunction) => {
      const {
        employmentEndDate,
        lastWorkingDate,
        deactivateGreenhouse,
        suspendGoogleAccount,
        suspendEloomiAccount,
        directReportManagerChangeReservations,
        performanceReviewExclusion: _performanceReviewExclusion,
        performanceReviewExclusionChanged: _performanceReviewExclusionChanged,
        ...restFormData
      } = formData;
      await updateRecord({
        variables: {
          input: {
            ...restFormData,
            employeeId: employeeId,
            performanceReviewExclusion: toPerformanceReviewExclusionInput(formData),
            employmentEndDate: StringUtils.emptyToNull(employmentEndDate),
            lastWorkingDate: StringUtils.emptyToNull(lastWorkingDate),
            doNotDeactivate: {
              greenhouse: deactivateGreenhouse,
              google: suspendGoogleAccount,
              eloomi: suspendEloomiAccount,
            },
            // directReportManagerChangeReservations will be accepted by the backend once the schema is deployed.
            ...(directReportManagerChangeReservations !== undefined && {
              directReportManagerChangeReservations: directReportManagerChangeReservations.map(r => ({
                directReportEmployeeId: r.directReportEmployeeId,
                changePrimaryManagerId: r.changePrimaryManagerId,
                changeDottedLineManagerId: r.changeDottedLineManagerId,
                newPrimaryManagerId: r.newPrimaryManagerId ?? undefined,
                newDottedLineManagerId: r.newDottedLineManagerId ?? undefined,
              })),
            }),
          },
        },
        onCompleted: data => {
          if (!data.updateEmployeeOffBoarding) {
            toast.error();
            return;
          }
          onCompleted();
          onSuccess();
        },
      });
    },
    [updateRecord, employeeId, onCompleted],
  );

  return {
    onOffBoardingUpdate,
    updating,
  };
};
